import type { Client } from '../../api/client.js';
import { ErreurApi } from '../../api/client.js';
import { chargerRequete, type EtatRequete } from '../../api/requete.js';
import type { DossierClerc, Interrogation, PieceDossier } from './types.js';

export interface DonneesClerc {
  dossiers: DossierClerc[];
  interrogations: Interrogation[];
  pieces: PieceDossier[];
}

/** Une pièce réservée à l’équipe n’atteint jamais un espace externe, même si le serveur l’envoie. */
export const piecesVisiblesParLeNotaire = (pieces: readonly PieceDossier[]): PieceDossier[] =>
  pieces.filter((p) => p.visibility === 'notaire');

/** Dossier mis en avant : le premier prêt pour ouverture, à défaut le premier transmis. */
export const dossierRetenu = (dossiers: readonly DossierClerc[]): DossierClerc | undefined =>
  dossiers.find((d) => d.readyForOpening) ?? dossiers[0];

export async function chargerEspaceClerc(client: Client): Promise<EtatRequete<DonneesClerc>> {
  const liste = await chargerRequete<DossierClerc[]>(client, 'clerc.dossiers');
  if (liste.etat === 'erreur') return liste;
  if (liste.etat !== 'ok') return { etat: 'vide' };
  const dossiers = liste.donnees;
  const retenu = dossierRetenu(dossiers);
  if (!retenu) return { etat: 'vide' };
  const parametres = { dossierId: retenu.id };
  const interrogations = await chargerRequete<Interrogation[]>(client, 'notaire.interrogations', { parametres });
  if (interrogations.etat === 'erreur') return interrogations;
  const pieces = await chargerRequete<PieceDossier[]>(client, 'documents.lister', { parametres });
  if (pieces.etat === 'erreur') return pieces;
  return {
    etat: 'ok',
    donnees: {
      dossiers,
      interrogations: interrogations.etat === 'ok' ? interrogations.donnees : [],
      pieces: pieces.etat === 'ok' ? piecesVisiblesParLeNotaire(pieces.donnees) : [],
    },
  };
}

export interface FichierADeposer {
  nom: string;
  mime: string;
  taille: number;
  octets: Blob;
}

/**
 * Dépôt d’un retour : ouvre la session reprenable puis envoie les octets (tus, un seul PATCH à l’offset 0).
 * Vrai seulement si le serveur a accepté les octets ; jamais « déposé » sur la seule foi de la session ouverte.
 */
export async function deposerRetour(
  client: Client,
  fetchBrut: typeof fetch,
  dossierId: string,
  fichier: FichierADeposer,
): Promise<boolean> {
  const session = await ouvrirDepotRetour(client, dossierId, fichier);
  if (!session) return false;
  try {
    const reponse = await fetchBrut(session.uploadUrl, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'Tus-Resumable': '1.0.0', 'Upload-Offset': '0', 'Content-Type': 'application/offset+octet-stream' },
      body: fichier.octets,
    });
    return reponse.ok;
  } catch {
    return false;
  }
}

export interface ApresEcriture {
  /** Message à annoncer à la clerc, ou `null` quand rien n’a été tenté. */
  message: string | null;
  /** État à afficher ensuite (rechargé après une écriture tentée). */
  etat: EtatRequete<DonneesClerc>;
}

/** Marquage d’une interrogation : cible, message de succès ou de conflit, rechargement. */
export async function apresMarquage(
  client: Client,
  etat: EtatRequete<DonneesClerc>,
  index: number,
  statut: 'envoyee' | 'revenue',
  jour: string,
): Promise<ApresEcriture> {
  if (etat.etat !== 'ok') return { message: null, etat };
  const cible = etat.donnees.interrogations[index];
  if (!cible) return { message: null, etat };
  let maj: Interrogation | null;
  try {
    maj = await marquerInterrogation(client, cible, statut, jour);
  } catch (e) {
    if (e instanceof ErreurApi && e.code === 'IMPERSONATION_READ_ONLY') return { message: e.messageUtilisateur, etat };
    throw e;
  }
  const message = maj
    ? 'Enregistré. Le Cabinet MAAT voit l’avancement de cette interrogation.'
    : 'Cette interrogation a été modifiée par une autre personne ou n’est plus accessible. Rechargez la page, puis recommencez.';
  return { message, etat: await chargerEspaceClerc(client) };
}

/** Dépôt d’un retour sur le dossier retenu : message de succès ou d’échec. */
export async function apresDepot(
  client: Client,
  fetchBrut: typeof fetch,
  etat: EtatRequete<DonneesClerc>,
  fichier: FichierADeposer,
): Promise<{ message: string | null }> {
  if (etat.etat !== 'ok') return { message: null };
  const retenu = dossierRetenu(etat.donnees.dossiers);
  if (!retenu) return { message: null };
  const depose = await deposerRetour(client, fetchBrut, retenu.id, fichier);
  return {
    message: depose
      ? 'Votre retour est déposé. Le Cabinet MAAT en est informé.'
      : 'Le dépôt n’a pas abouti. Réessayez dans un instant ; si cela persiste, contactez le Cabinet MAAT.',
  };
}

/** Marque une interrogation envoyée ou revenue ; `null` si le serveur refuse (conflit de version compris). */
export async function marquerInterrogation(
  client: Client,
  i: Interrogation,
  statut: 'envoyee' | 'revenue',
  jour: string,
): Promise<Interrogation | null> {
  const corps = statut === 'envoyee' ? { status: statut, sentAt: jour } : { status: statut, returnedAt: jour };
  try {
    return await client.appeler<Interrogation>('notaire.interrogations.modifier', {
      parametres: { interrogationId: i.id },
      corps,
      versionLigne: i.rowVersion,
    });
  } catch (e) {
    // ARB-CLERC-17 : un refus « lecture seule » (voir en tant que) n'est pas un conflit : il remonte avec son propre message.
    if (e instanceof ErreurApi && e.code !== 'IMPERSONATION_READ_ONLY') return null;
    throw e;
  }
}

export async function ouvrirDepotRetour(
  client: Client,
  dossierId: string,
  fichier: { nom: string; mime: string; taille: number },
): Promise<{ uploadUrl: string } | null> {
  try {
    const session = await client.appeler<{ uploadUrl: string }>('documents.televerser', {
      parametres: { dossierId },
      corps: {
        title: fichier.nom,
        category: 'retour_notaire',
        mime: fichier.mime,
        size: fichier.taille,
        links: [{ targetType: 'dossier', targetId: dossierId }],
      },
    });
    return { uploadUrl: session.uploadUrl };
  } catch (e) {
    if (e instanceof ErreurApi) return null;
    throw e;
  }
}
